<template>
  <div class="weather-effect" ref="container">
    <canvas ref="canvas"></canvas>
    <div v-show="isThundering" class="thunder-flash"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  mouseX: {
    type: Number,
    default: 0
  },
  mouseY: {
    type: Number,
    default: 0
  }
});

const container = ref(null);
const canvas = ref(null);
const isThundering = ref(false);
let ctx = null;
let animationFrameId = null;
let raindrops = [];
let lightningBolts = [];
let width = 0;
let height = 0;

// 雨滴类
class Raindrop {
  constructor() {
    this.x = Math.random() * width;
    this.y = Math.random() * -height;
    this.speed = Math.random() * 5 + 10;
    this.length = Math.random() * 20 + 10;
    this.opacity = Math.random() * 0.5 + 0.1;
  }

  update() {
    this.y += this.speed;
    if (this.y > height) {
      this.y = Math.random() * -100;
      this.x = Math.random() * width;
    }
  }

  draw() {
    ctx.beginPath();
    ctx.strokeStyle = `rgba(174, 194, 224, ${this.opacity})`;
    ctx.lineWidth = 1;
    ctx.moveTo(this.x, this.y);
    ctx.lineTo(this.x, this.y + this.length);
    ctx.stroke();
  }
}

// 闪电类
class LightningBolt {
  constructor(startX) {
    this.segments = [];
    this.alpha = 1;
    this.life = 0;
    this.maxLife = 20; // 闪电存在帧数
    this.generate(startX, 0);
  }

  generate(x, y) {
    let currentX = x;
    let currentY = y;
    
    while (currentY < height) {
      // 随机生成下一个点，模拟闪电折线
      const nextX = currentX + (Math.random() * 60 - 30); // 横向偏移范围 -30 到 30
      const nextY = currentY + (Math.random() * 40 + 20); // 纵向步长 20 到 60
      
      this.segments.push({
        x1: currentX,
        y1: currentY,
        x2: nextX,
        y2: nextY
      });

      // 概率生成分支闪电
      if (Math.random() < 0.2) {
        this.generateBranch(currentX, currentY);
      }

      currentX = nextX;
      currentY = nextY;
    }
  }

  generateBranch(x, y) {
    let currentX = x;
    let currentY = y;
    const branchLength = Math.random() * 100 + 50; // 分支长度
    let length = 0;

    while (length < branchLength && currentY < height) {
      const nextX = currentX + (Math.random() * 40 - 20);
      const nextY = currentY + (Math.random() * 30 + 10);
      
      this.segments.push({
        x1: currentX,
        y1: currentY,
        x2: nextX,
        y2: nextY,
        isBranch: true
      });

      length += Math.sqrt(Math.pow(nextX - currentX, 2) + Math.pow(nextY - currentY, 2));
      currentX = nextX;
      currentY = nextY;
    }
  }

  update() {
    this.life++;
    // 闪烁效果
    if (this.life < 5) {
      this.alpha = Math.random() > 0.5 ? 1 : 0.5;
    } else {
      this.alpha -= 0.05; // 渐隐
    }
    return this.alpha > 0;
  }

  draw() {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // 绘制辉光
    ctx.shadowBlur = 15;
    ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
    
    this.segments.forEach(seg => {
      ctx.beginPath();
      ctx.strokeStyle = `rgba(255, 255, 255, ${this.alpha * (seg.isBranch ? 0.6 : 1)})`;
      ctx.lineWidth = seg.isBranch ? 1 : 2.5;
      ctx.moveTo(seg.x1, seg.y1);
      ctx.lineTo(seg.x2, seg.y2);
      ctx.stroke();
    });
    
    ctx.restore();
  }
}

// 初始化雨滴
const initRain = () => {
  raindrops = [];
  const count = Math.floor(width / 4);
  for (let i = 0; i < count; i++) {
    raindrops.push(new Raindrop());
  }
};

// 动画循环
const animate = () => {
  ctx.clearRect(0, 0, width, height);
  
  // 绘制雨滴
  raindrops.forEach(drop => {
    drop.update();
    drop.draw();
  });

  // 绘制和更新闪电
  for (let i = lightningBolts.length - 1; i >= 0; i--) {
    const bolt = lightningBolts[i];
    bolt.draw();
    if (!bolt.update()) {
      lightningBolts.splice(i, 1);
      if (lightningBolts.length === 0) {
        isThundering.value = false;
      }
    }
  }

  animationFrameId = requestAnimationFrame(animate);
};

// 调整大小
const handleResize = () => {
  if (container.value && canvas.value) {
    width = container.value.clientWidth;
    height = container.value.clientHeight;
    canvas.value.width = width;
    canvas.value.height = height;
    initRain();
  }
};

// 触发打雷
const triggerThunder = (x) => {
  isThundering.value = true;
  // 闪电起始位置基于鼠标横向位置，带随机偏移
  const startX = x + (Math.random() * 200 - 100);
  lightningBolts.push(new LightningBolt(startX));
};

// 监听鼠标移动触发打雷
let lastThunderTime = 0;
watch(() => props.mouseX, (newX) => {
  const now = Date.now();
  // 每 2 秒最多触发一次，且有 30% 概率触发
  if (now - lastThunderTime > 2000 && Math.random() > 0.7) {
    triggerThunder(newX);
    lastThunderTime = now;
  }
});

onMounted(() => {
  if (canvas.value) {
    ctx = canvas.value.getContext('2d');
    window.addEventListener('resize', handleResize);
    handleResize();
    animate();
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
  }
});
</script>

<style scoped>
.weather-effect {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.thunder-flash {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.15); /* 全屏瞬间闪光 */
  opacity: 0;
  animation: flash 0.2s ease-out;
  pointer-events: none;
}

@keyframes flash {
  0% { opacity: 1; }
  100% { opacity: 0; }
}
</style>
